import Psd, { NodeChild } from '@webtoon/psd';
import { getImageFromPsdEncodedData } from '@/components/Layers/utils';
import { ImportLayersElements, LayerElementsMetadata } from '@/typings/layers/layers';
import { imageToBlob, uploadImageToS3 } from '@/utils/imageUtils';
import { ChildrenType, TreeType } from '@/components/Layers/modal/types';
import { IFileParserStrategy } from '@/typings/parsers/externalFileParser';
import { FileParserType } from '@/typings/parsers/fileParserTypes';

export class PsdFileParser implements IFileParserStrategy<Psd> {
    type: FileParserType = 'PSD';
    page = 1;

    constructor(page = 1) {
        this.page = page;
    }

    parse(buffer: ArrayBuffer): Promise<Psd> {
        const psdFile = Psd.parse(buffer);
        return Promise.resolve(psdFile);
    }

    async extractLayers(psdFile: Psd, importLayers: ImportLayersElements): Promise<ImportLayersElements> {
        const layerTree = psdFile;
        const { children, height, width } = layerTree;
        importLayers.pages = [
            { pageNumber: 1, name: 'Page 1', elements: [], artWorkHeight: width, artWorkWidth: height }
        ];

        const canvas = document.createElement('canvas');
        const artWorkEncodedData = await layerTree.composite(true, false);
        const artWorkThumbnailResize = { width: 50, height: 50 };
        const artWorkBase64 = getImageFromPsdEncodedData(
            canvas,
            artWorkEncodedData,
            width,
            height,
            artWorkThumbnailResize
        );

        const getThumbnail = await uploadImageToS3('thumbnail', artWorkBase64);
        importLayers.artWorkThumbnail = getThumbnail.Path;
        importLayers.type = this.type;
        importLayers.mode = 'LAYERS';
        for (const item of children) {
            const elements = await this.lookForElements(item, canvas);
            importLayers.pages[0].elements = importLayers.pages[0].elements.concat(elements);
        }

        canvas.remove();
        return importLayers;
    }

    private async lookForElements(
        layerTreeNode: NodeChild,
        canvas: HTMLCanvasElement
    ): Promise<LayerElementsMetadata[]> {
        let importLayersElements: LayerElementsMetadata[] = [];
        if (layerTreeNode.type === ChildrenType.Group) {
            for (const groupItem of layerTreeNode.children) {
                const childElements = await this.lookForElements(groupItem, canvas);
                importLayersElements = importLayersElements.concat(childElements);
            }
        } else {
            const baseProperties = {
                width: layerTreeNode.width,
                height: layerTreeNode.height,
                top: layerTreeNode.top,
                left: layerTreeNode.left,
                visible: !layerTreeNode.isHidden,
                name: layerTreeNode.name
            };

            if (layerTreeNode.text || layerTreeNode.textProperties) {
                importLayersElements.push({
                    ...baseProperties,
                    textValue: layerTreeNode.text,
                    type: TreeType.FROM_FILE_TEXT_ELEMENT
                });
            } else {
                canvas.width = layerTreeNode.width;
                canvas.height = layerTreeNode.height;
                const uInt8ClampedArray = await layerTreeNode.composite(true, false);
                const base64 = getImageFromPsdEncodedData(
                    canvas,
                    uInt8ClampedArray,
                    layerTreeNode.width,
                    layerTreeNode.height
                );
                const blobImage = await imageToBlob(base64);

                importLayersElements.push({
                    ...baseProperties,
                    imgSrc: blobImage,
                    type: TreeType.FROM_FILE_IMAGE_ELEMENT
                });
            }
        }

        return importLayersElements;
    }
}
